import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { Badge, BadgeType } from "../types";

interface BadgeCelebrationProps {
  open: boolean;
  onClose: () => void;
  playerName: string;
  newBadges: Badge[];
}

const BADGE_INFO: Record<
  BadgeType,
  { name: string; description: string; emoji: string; color: string }
> = {
  first_win: {
    name: "First Victory",
    description: "Won your first match",
    emoji: "🎉",
    color: "#4CAF50",
  },
  giant_killer: {
    name: "Giant Killer",
    description: "Defeated a player ranked 5+ positions higher",
    emoji: "⚔️",
    color: "#F44336",
  },
  win_streak_10: {
    name: "Unstoppable",
    description: "Won 10 matches in a row",
    emoji: "🔥",
    color: "#FF5722",
  },
  division_champion: {
    name: "Division Champion",
    description: "Finished #1 in your division",
    emoji: "👑",
    color: "#FFD700",
  },
  close_call: {
    name: "Close Call",
    description: "Won a match by a narrow margin (4-3 or 5-4)",
    emoji: "💪",
    color: "#9C27B0",
  },
  perfect_day: {
    name: "Perfect Day",
    description: "Won all 4 matches in a league day",
    emoji: "⭐",
    color: "#FFC107",
  },
  veteran: {
    name: "Veteran",
    description: "Played 50+ league days",
    emoji: "🏅",
    color: "#607D8B",
  },
  partner_carry: {
    name: "Partner Carry",
    description: "Won with a partner ranked 10+ positions lower",
    emoji: "🎯",
    color: "#00BCD4",
  },
  clean_sweep: {
    name: "Clean Sweep",
    description: "Won a match 4-0 or 5-0",
    emoji: "🧹",
    color: "#8BC34A",
  },
  mr_ms_consistent: {
    name: "Mr/Ms Consistent",
    description: "Won exactly 2 games in 5 consecutive matches",
    emoji: "🎲",
    color: "#FF9800",
  },
  century: {
    name: "Century",
    description: "Won 100+ games in a season with positive game difference",
    emoji: "💯",
    color: "#E91E63",
  },
  iron_man: {
    name: "Iron Man",
    description: "Played 10 consecutive league days",
    emoji: "🦾",
    color: "#FF6B35",
  },
  all_season_warrior: {
    name: "All-Season Warrior",
    description: "Played every league day in a season",
    emoji: "⚔️",
    color: "#673AB7",
  },
  division_hopper: {
    name: "Division Hopper",
    description: "Played in 3+ different divisions",
    emoji: "🔀",
    color: "#009688",
  },
  unlucky: {
    name: "Unlucky",
    description: "Lost 5 matches by close margins (3-4 or 4-5)",
    emoji: "😅",
    color: "#795548",
  },
  sp_team: {
    name: "SP Team",
    description: "Stuttgart Padel League family & friends",
    emoji: "🏆",
    color: "#1976D2",
  },
};

function BadgeCelebration({
  open,
  onClose,
  playerName,
  newBadges,
}: BadgeCelebrationProps) {
  const [currentBadgeIndex, setCurrentBadgeIndex] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (open) {
      setCurrentBadgeIndex(0);
      setShowConfetti(true);
      // Stop confetti after 3 seconds
      setTimeout(() => setShowConfetti(false), 3000);
    }
  }, [open]);

  if (newBadges.length === 0) return null;

  const currentBadge = newBadges[currentBadgeIndex];
  const badgeInfo = BADGE_INFO[currentBadge.type];
  const isMultipleBadges = newBadges.length > 1;

  const handleNext = () => {
    if (currentBadgeIndex < newBadges.length - 1) {
      setCurrentBadgeIndex(currentBadgeIndex + 1);
    } else {
      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentBadgeIndex > 0) {
      setCurrentBadgeIndex(currentBadgeIndex - 1);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          position: "relative",
          overflow: "visible",
        },
      }}
    >
      {/* Close button — zIndex must beat DialogContent's zIndex:2 */}
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: "white",
          zIndex: 10,
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Confetti effect */}
      {showConfetti && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
            zIndex: 1,
            overflow: "hidden",
          }}
        >
          {[...Array(30)].map((_, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                top: "-10px",
                left: `${Math.random() * 100}%`,
                width: "10px",
                height: "10px",
                backgroundColor: [
                  "#FFD700",
                  "#FF6B6B",
                  "#4ECDC4",
                  "#95E1D3",
                  "#F38181",
                ][Math.floor(Math.random() * 5)],
                animation: `fall ${2 + Math.random() * 3}s linear infinite`,
                animationDelay: `${Math.random() * 2}s`,
                "@keyframes fall": {
                  "0%": {
                    transform: "translateY(0) rotate(0deg)",
                    opacity: 1,
                  },
                  "100%": {
                    transform: `translateY(120vh) rotate(${
                      360 + Math.random() * 360
                    }deg)`,
                    opacity: 0,
                  },
                },
              }}
            />
          ))}
        </Box>
      )}

      <DialogContent
        sx={{
          textAlign: "center",
          py: { xs: 3, sm: 6 },
          px: { xs: 2, sm: 4 },
          pt: { xs: 5, sm: 6 }, // extra top so close button doesn't overlap text
          position: "relative",
          zIndex: 2,
          maxHeight: "85dvh",
          overflowY: "auto",
        }}
      >
        {/* Congratulations Header */}
        <Typography
          sx={{
            fontWeight: "bold",
            mb: 1,
            fontSize: { xs: "1.5rem", sm: "2.5rem" },
            textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
            animation: "bounce 0.6s ease-in-out",
            "@keyframes bounce": {
              "0%, 100%": { transform: "translateY(0)" },
              "50%": { transform: "translateY(-10px)" },
            },
          }}
        >
          🎊 Congratulations! 🎊
        </Typography>

        <Typography
          sx={{ mb: { xs: 2, sm: 4 }, opacity: 0.95, fontSize: { xs: "1rem", sm: "1.5rem" } }}
        >
          {playerName}
        </Typography>

        {/* Badge Display */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
            mb: 4,
          }}
        >
          <Chip
            label={badgeInfo.emoji}
            sx={{
              backgroundColor: badgeInfo.color,
              color: "white",
              fontSize: { xs: "3rem", sm: "5rem" },
              fontWeight: "bold",
              width: { xs: "100px", sm: "150px" },
              height: { xs: "100px", sm: "150px" },
              borderRadius: "50%",
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
              animation: "pulse 2s ease-in-out infinite",
              "@keyframes pulse": {
                "0%, 100%": { transform: "scale(1)" },
                "50%": { transform: "scale(1.05)" },
              },
            }}
          />

          <Box>
            <Typography
              sx={{ fontWeight: "bold", mb: 1, fontSize: { xs: "1.25rem", sm: "2rem" }, textShadow: "1px 1px 2px rgba(0,0,0,0.2)" }}
            >
              {badgeInfo.name}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              {badgeInfo.description}
            </Typography>
          </Box>

          {/* Badge metadata */}
          {currentBadge.metadata && (
            <Box sx={{ mt: 2, opacity: 0.85 }}>
              {currentBadge.metadata.opponentName && (
                <Typography variant="body2">
                  Beat {currentBadge.metadata.opponentName} (Ranked{" "}
                  {currentBadge.metadata.rankDifference}+ positions higher)
                </Typography>
              )}
              {currentBadge.metadata.partnerName && (
                <Typography variant="body2">
                  With partner: {currentBadge.metadata.partnerName}
                </Typography>
              )}
              {currentBadge.metadata.streakLength && (
                <Typography variant="body2">
                  {currentBadge.metadata.streakLength} game streak!
                </Typography>
              )}
              {currentBadge.metadata.totalGames && (
                <Typography variant="body2">
                  {currentBadge.metadata.totalGames} games won!
                </Typography>
              )}
              {currentBadge.metadata.consecutiveDays && (
                <Typography variant="body2">
                  {currentBadge.metadata.consecutiveDays} consecutive league days!
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Navigation for multiple badges */}
        {isMultipleBadges && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
              Badge {currentBadgeIndex + 1} of {newBadges.length}
            </Typography>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
              <Button
                variant="outlined"
                onClick={handlePrevious}
                disabled={currentBadgeIndex === 0}
                sx={{
                  color: "white",
                  borderColor: "white",
                  "&:hover": {
                    borderColor: "white",
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                onClick={handleNext}
                sx={{
                  color: "white",
                  borderColor: "white",
                  "&:hover": {
                    borderColor: "white",
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                {currentBadgeIndex < newBadges.length - 1 ? "Next" : "Done"}
              </Button>
            </Box>
          </Box>
        )}

        {/* Close button for single badge */}
        {!isMultipleBadges && (
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              backgroundColor: "white",
              color: "#667eea",
              fontWeight: "bold",
              px: 4,
              py: 1.5,
              fontSize: "1.1rem",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.9)",
              },
            }}
          >
            Awesome! 🎉
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default BadgeCelebration;
